import React, { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import { useSelected } from 'contexts/selected';
import { useIntl } from 'react-intl';
import SlideOutMenu from 'components/SlideOutMenu/SlideOutMenu';
import Title, { HeadingType } from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Button from 'ui/Button/Button';
import ArrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import CrossIcon from '@ingka/ssr-icon/paths/cross';
import { ButtonTypeEnum } from 'ui/Button/Button';
import {
  Widget,
  SelectedMenuProductList as SelectedMenuProductListType,
  SelectedMenuContent
} from 'rangeConfig/@types';
import isWidget from 'rangeConfig/shared/utils/isWidget';
import ProductList from 'selectedMenu/ProductList';
import { useCore } from 'contexts/core';
import { getMenuTitle } from '../utils/utils';
import { getMarketProducts } from 'ducks/products';
import { useSelector } from 'react-redux';
import Divider from 'ui/Divider/Divider';
import SelectedMenuProductList from './SelectedMenuProductList';
import Box from 'ui/Box';
import { WidgetContext, WidgetContextValue } from 'contexts/widget';

const SelectedMenuWrapper = styled.div`
  height: 100%;
  padding: 0 ${({ theme }) => theme.space.xxl};
  overflow-y: auto;
`;

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 200;
  background: #fff;
  padding-top: ${({ theme }) => theme.space.md};
  padding-bottom: ${({ theme }) => theme.space.xs};
`;

const HeaderFlex = styled.div`
  display: flex;
  align-items: center;
  &[data-space-between='true'] {
    justify-content: space-between;
  }
`;

type HeaderProps = {
  title?: string;
  onBack?: () => void;
  onClose?: () => void;
};

const Header = ({ title, onBack, onClose }: HeaderProps) => {
  const { formatMessage } = useIntl();
  return (
    <HeaderWrapper>
      <HeaderFlex data-space-between={!onBack}>
        {onBack && (
          <Button
            mr="xs"
            type={ButtonTypeEnum.tertiary}
            ssrIcon={ArrowLeftIcon}
            aria-label={formatMessage({ id: 'storageone.back' })}
            onClick={onBack}
            iconOnly
          />
        )}
        <Title type={ThemeFontStyleTypeEnum.headingMedium}>{title}</Title>
        {onClose && (
          <Button
            dataTestId="selected-menu-close-button"
            type={ButtonTypeEnum.tertiary}
            onClick={onClose}
            ssrIcon={CrossIcon}
            aria-label={formatMessage({ id: 'storageone.back' })}
            iconOnly
          />
        )}
      </HeaderFlex>
    </HeaderWrapper>
  );
};

const wrapInContext = (value?: WidgetContextValue) => ({
  children
}: {
  children: React.ReactNode;
}) =>
  value ? <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider> : children;

const AdditionalContent = () => {
  const coreApi = useCore();
  const { additionalContent, toggleAdditionalContent, widgetContext } = useSelected();
  const { formatMessage } = useIntl();
  const title = getMenuTitle(
    additionalContent?.title,
    additionalContent?.titleValue,
    formatMessage,
    coreApi
  );

  const CtxWrapper = useMemo(
    () => wrapInContext(widgetContext && { ...widgetContext, expanded: true }),
    [widgetContext]
  );

  return (
    <SlideOutMenu trigger={additionalContent !== undefined}>
      <SelectedMenuWrapper>
        <Header title={title} onBack={() => toggleAdditionalContent(undefined)} />
        {additionalContent && (
          <CtxWrapper>
            <ProductList
              alternativeProductList={additionalContent.alternativeSelectedProductList}
              content={additionalContent}
            />
          </CtxWrapper>
        )}
      </SelectedMenuWrapper>
    </SlideOutMenu>
  );
};

const SelectedMenu = () => {
  const { visible, selectedMenu, unselect, hiddenContent, getContentProducts } = useSelected();
  const coreApi = useCore();
  const content = selectedMenu?.content || [];
  const { formatMessage } = useIntl();
  const title = getMenuTitle(selectedMenu?.menuTitle, undefined, formatMessage, coreApi);

  const products = useSelector(getMarketProducts);

  const widgets: Widget[] = (content?.filter(c => isWidget(c)) as Widget[]) || [];
  const visibleWidgets = widgets.filter(widget =>
    widget.hidden ? !widget.hidden(coreApi, products) : true
  );
  const visibleContent = content
    .map(content => {
      if (isWidget(content)) {
        return (content as Widget).hidden && (content as Widget).hidden!(coreApi, products)
          ? null
          : content;
      } else {
        const contentProducts = getContentProducts(content as SelectedMenuProductListType);
        return hiddenContent(content as SelectedMenuProductListType) || contentProducts.length === 0
          ? null
          : content;
      }
    })
    .filter(Boolean) as SelectedMenuContent[];

  return (
    <SlideOutMenu trigger={visible}>
      <SelectedMenuWrapper>
        <Header title={title} onClose={unselect} />
        {visibleContent.map((item, index) => {
          if (isWidget(item)) {
            const { component: Component } = item as Widget;
            const subTitle = getMenuTitle(item.title, undefined, formatMessage, coreApi);
            return (
              <Fragment key={index}>
                <Box mt="md" mb="md">
                  <Title
                    heading={HeadingType.HeadingThree}
                    type={ThemeFontStyleTypeEnum.headingSmall}
                  >
                    {subTitle}
                  </Title>
                  <Component />
                </Box>
                {index !== visibleContent.length - 1 && <Divider />}
              </Fragment>
            );
          } else {
            const skipThumbs = visibleContent.length === 1 && visibleWidgets?.length === 0;
            const noThumbs = skipThumbs
              ? () => true
              : (item as SelectedMenuProductListType).noThumbs;
            const title = skipThumbs ? '' : item.title;

            return (
              <Fragment key={index}>
                <SelectedMenuProductList
                  key={item.id}
                  content={{ ...item, title, noThumbs } as SelectedMenuProductListType}
                />
                {index !== visibleContent.length - 1 && <Divider />}
              </Fragment>
            );
          }
        })}
        <AdditionalContent />
      </SelectedMenuWrapper>
    </SlideOutMenu>
  );
};

export default SelectedMenu;
